import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronLastRightSvg, ChevronRightSvg } from '../../assets/svgs';
import { PageDropdownContainer, PaginationButton, PaginationContainer, PaginationWrapper } from './pagination.style';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

const PaginationComponent = ({
    // Only React-Table props
    page = 0,
    pages = 4,
    canPrevious = false,
    canNext = true,
    onPreviousPageClick = () => { },
    onNextPageClick = () => { },
    onPageChange = () => { },
}) => {
    // Initialize state
    const [states, setStates] = useState({
        currentPage: page,
        totalPages: pages,
        pageSize: 10 // Default page size
    });

    const [disabled, setDisabled] = useState({
        start: !canPrevious,
        end: !canNext,
    });

    const handleClick = (e) => {
        if (states.currentPage !== e) {
            setStates((old) => ({ ...old, currentPage: e }));
            onPageChange(e);
        }
    };

    const handleNextPrev = (e) => {
        setStates((old) => ({ ...old, currentPage: e }));
        if (e > states.currentPage) {
            // onNextPageClick();
            onPageChange(e);
        } else {
            // onPreviousPageClick();
            onPageChange(e);
        }
    };

    const handleFirstLast = (e) => {
        setStates((old) => ({ ...old, currentPage: e }));
        onPageChange(e);
    };

    // Update state when props change
    useEffect(() => {
        setStates((old) => ({
            ...old,
            currentPage: page,
            totalPages: pages
        }));
        setDisabled({
            start: !canPrevious,
            end: !canNext
        });
    }, [page, pages, canPrevious, canNext]);

    const NEXT_PREV_BUTTON = ({ rotate, ...props }) => (
        <PaginationButton className="pagination-action" {...props}>
            <ChevronRightSvg style={{ transform: `rotate(${rotate}deg)` }} />
        </PaginationButton>
    );

    const FIRST_LAST_BUTTON = ({ rotate, ...props }) => (
        <PaginationButton className="pagination-button" {...props}>
            <ChevronLastRightSvg style={{ transform: `rotate(${rotate}deg)` }} />
        </PaginationButton>
    );

    const RenderPageButtons = () => {
        const totalPages = states.totalPages;
        const currentPage = states.currentPage;

        if (totalPages <= 5) {
            return [...Array(totalPages)].map((_, index) => (
                <PaginationButton
                    key={index}
                    onClick={() => handleClick(index)}
                    className={currentPage === index ? 'pagination-item-selected' : 'pagination-item'}
                >
                    {index + 1}
                </PaginationButton>
            ));
        }

        const buttons = [];
        if (currentPage <= 2) {
            for (let i = 0; i < 3; i++) {
                buttons.push(
                    <PaginationButton
                        key={i}
                        onClick={() => handleClick(i)}
                        className={currentPage === i ? 'pagination-item-selected' : 'pagination-item'}
                    >
                        {i + 1}
                    </PaginationButton>
                );
            }
            buttons.push(<span key="dots1">...</span>);
            buttons.push(
                <PaginationButton
                    key={totalPages - 1}
                    onClick={() => handleClick(totalPages - 1)}
                    className={currentPage === totalPages - 1 ? 'pagination-item-selected' : 'pagination-item'}
                >
                    {totalPages}
                </PaginationButton>
            );
        } else if (currentPage >= totalPages - 3) {
            buttons.push(
                <PaginationButton
                    key={0}
                    onClick={() => handleClick(0)}
                    className={currentPage === 0 ? 'pagination-item-selected' : 'pagination-item'}
                >
                    1
                </PaginationButton>
            );
            buttons.push(<span key="dots2">...</span>);
            for (let i = totalPages - 3; i < totalPages; i++) {
                buttons.push(
                    <PaginationButton
                        key={i}
                        onClick={() => handleClick(i)}
                        className={currentPage === i ? 'pagination-item-selected' : 'pagination-item'}
                    >
                        {i + 1}
                    </PaginationButton>
                );
            }
        } else {
            buttons.push(
                <PaginationButton
                    key={0}
                    onClick={() => handleClick(0)}
                    className={currentPage === 0 ? 'pagination-item-selected' : 'pagination-item'}
                >
                    1
                </PaginationButton>
            );
            buttons.push(<span key="dots1">...</span>);
            for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                buttons.push(
                    <PaginationButton
                        key={i}
                        onClick={() => handleClick(i)}
                        className={currentPage === i ? 'pagination-item-selected' : 'pagination-item'}
                    >
                        {i + 1}
                    </PaginationButton>
                );
            }
            buttons.push(<span key="dots2">...</span>);
            buttons.push(
                <PaginationButton
                    key={totalPages - 1}
                    onClick={() => handleClick(totalPages - 1)}
                    className={currentPage === totalPages - 1 ? 'pagination-item-selected' : 'pagination-item'}
                >
                    {totalPages}
                </PaginationButton>
            );
        }
        return buttons;
    };

    return (
        <PaginationWrapper $show={pages > 1}>
            <PaginationContainer>
                {
                    states && states.totalPages > 1 && (
                        <Fragment>
                            {FIRST_LAST_BUTTON({ rotate: 180, onClick: () => handleFirstLast(0), disabled: disabled.start })}
                            {NEXT_PREV_BUTTON({ rotate: 180, onClick: () => handleNextPrev(states.currentPage - 1), disabled: disabled.start })}

                            {RenderPageButtons()}

                            {NEXT_PREV_BUTTON({ onClick: () => handleNextPrev(states.currentPage + 1), disabled: disabled.end })}
                            {FIRST_LAST_BUTTON({ onClick: () => handleFirstLast(states.totalPages - 1), disabled: disabled.end })}
                        </Fragment>
                    )
                }
            </PaginationContainer>
            <PageDropdownContainer>
                <p>Page</p>
                <FormControl size="small">
                    <Select
                        value={states.currentPage + 1}
                        onChange={(e) => onPageChange(e.target.value - 1)}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Without label' }}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {
                            Array.from({ length: pages }, (_, i) => i + 1).map((num) => (
                                <MenuItem key={num} value={num}>
                                    {num}
                                </MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
                <p>of {pages}</p>
            </PageDropdownContainer>

        </PaginationWrapper>
    );
};

PaginationComponent.propTypes = {
    page: PropTypes.number,
    pages: PropTypes.number,
    canPrevious: PropTypes.bool,
    canNext: PropTypes.bool,
    onPreviousPageClick: PropTypes.func,
    onNextPageClick: PropTypes.func,
    onPageChange: PropTypes.func,
};

export default PaginationComponent;
